import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { newPaginationInput } from "src/api/types";
import { useModalAction } from "src/components/modal-views/context";
import GlobalContext from "src/context/global-context";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { getErrorMessage } from "src/lib/utils";
import { Table } from "antd";
import client from "../../api";

const CardAllTransactions = () => {
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const { openModal, closeModal } = useModalAction();

  const { canView, canList } = usePermissions(
    permissionConstants.CARD_TRANSACTIONS
  );
  const canViewTransactions = canView || canList;

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [searchParams, setSearchParams] = useState<newPaginationInput>({
    limit: LIST_PAGE_SIZE,
    page: 1,
  });
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState<boolean>(false);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }
  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);
  const { mutate: getAllCardTransactions, isLoading } = useMutation(
    client.cardTransactions.getAllCardTransactions,
    {
      onSuccess: (res: any) => {
        const dataObj = res?.data?.data || [];
        const pagination = res?.data?.pagination || {};

        setTotal(pagination.total || 0);

        setDataSource(
          dataObj.map((item: any) => ({
            key: item.id,
            id: item.id,
            card_id: item.card_id,
            transaction_id: item.transaction_id,
            merchant_id: item.merchant_id,
            masked_pan: item?.card?.masked_pan,
            merchant_name: item.merchant_name,
            amount: item.amount,
            currency: item.currency,
            type: item.type,
            status: item.status,
            oper_date: item.oper_date,
            createdAt: item.createdAt,
            settlement_date: item.settlement_date,
          })),
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: getCardUserDetails, isLoading: isLoadingCardUserDetails } =
    useMutation(client.userVirtualCards.getUserDetails, {
      onSuccess: (res: any) => {
        openModal("CARD_USER_DETAILS", {
          data: res?.data?.[0],
        });
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    });

  const onPageChange = (page: number, pageSize: number) => {
    setSearchParams({
      ...searchParams,
      page: page,
    });
  };

  useEffect(() => {
    if (!isMounted || !canViewTransactions) {
      return;
    }
    getAllCardTransactions({
      ...searchParams,
      // start_date: startDate
      //   ? moment(startDate).format("yyyy-MM-DD")
      //   : undefined,
      // end_date: endDate ? moment(endDate).format("yyyy-MM-DD") : undefined,
    });
  }, [isMounted, canViewTransactions, searchParams.page]);

  const columns = [
    {
      title: "Card ID",
      dataIndex: "card_id",
      render: (val: string, record: any) => (
        <span
          className="text-blue-500 cursor-pointer underline"
          onClick={() => {
            getCardUserDetails({
              id: record.card_id,
            });
          }}
        >
          {val}
        </span>
      ),
    },
    {
      title: "Transaction ID",
      dataIndex: "transaction_id",
    },
    {
      title: "Card number",
      dataIndex: "masked_pan",
    },
    {
      title: "Merchant name",
      dataIndex: "merchant_name",
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (val: string) => (
        <span
          className={`px-2 py-1 rounded text-white text-xs ${
            val === "debit" ? "bg-red-500" : "bg-green-500"
          }`}
        >
          {val}
        </span>
      ),
    },
    {
      title: "Amount",
      render: (_: any, record: any) => <span>{record.amount}</span>,
    },
     {
      title: "Currency",
      render: (_: any, record: any) => <span>{record.currency}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (val: string) => (
        <span
          className={`px-2 py-1 rounded text-white text-xs ${
            val === "success" ? "bg-green-500" : "bg-gray-400"
          }`}
        >
          {val}
        </span>
      ),
    },
    {
      title: "Operation Date",
      dataIndex: "oper_date",
      render: (val: string) => moment(val).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: "Settlement Date",
      dataIndex: "settlement_date",
      render: (val: string) => moment(val).format("YYYY-MM-DD HH:mm"),
    },
  ];

  return (
    <div className="p-6">
      <div className="bg-white rounded-md shadow-sm p-4">
        <h1 className="text-xl font-semibold mb-4">Card Transactions</h1>

        {/* <Table
          loading={isLoading}
          columns={columns}
          dataSource={dataSource}
          pagination={{
            total,
            current: searchParams.page,
            pageSize: searchParams.limit,
            onChange: (page) => setSearchParams((prev) => ({ ...prev, page })),
            showSizeChanger: false,
          }}
        /> */}
        {!canViewTransactions ? (
          <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-2 sm:mt-4 text-center shadow-sm">
            <p className="font-manrope text-[16px] font-semibold text-secondary">
              You do not have permission to view card transactions.
            </p>
          </div>
        ) : (
          <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
            <Table
              loading={isLoading}
              columns={columns}
              dataSource={dataSource}
              pagination={{
                total,
                current: searchParams.page,
                onChange: onPageChange,
                position: ["bottomCenter"],
                defaultPageSize: LIST_PAGE_SIZE,
                showSizeChanger: false,
              }}
              rowClassName="editable-row"
              // scroll={{ x: "max-content" }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CardAllTransactions;
